import React, { useEffect } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Link } from "react-router-dom"
import { AtSign, BadgeCheck, LayoutDashboard, Mail, Settings, UserRound } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { useUserProfile } from "../../hooks/useUserProfile"

const ProfileStat = ({ label, value }) => (
  <div className="tyk-stat">
    <span>{label}</span>
    <strong className="text-base">{value || "Not provided"}</strong>
  </div>
)

const Profile = () => {
  const { user, isAuthenticated, isLoading } = useAuth0()
  const { profile, error: profileError, loading: profileLoading } = useUserProfile()

  useEffect(() => {
    document.title = "Profile | ThousandYearKingdom.org"
  }, [])

  if (isLoading || profileLoading) {
    return (
      <main className="tyk-page">
        <div className="tyk-page-shell">
          <div className="tyk-panel">Loading profile...</div>
        </div>
      </main>
    )
  }

  if (!isAuthenticated || !user) {
    return (
      <main className="tyk-page">
        <div className="tyk-page-shell">
          <div className="tyk-panel">Not authenticated</div>
        </div>
      </main>
    )
  }

  if (profileError) {
    return (
      <main className="tyk-page">
        <div className="tyk-page-shell">
          <div className="tyk-panel text-red-200">{profileError}</div>
        </div>
      </main>
    )
  }

  const profileName = profile?.userFname && profile?.userLname
    ? `${profile.userFname} ${profile.userLname}`
    : user.name
  const profileEmail = profile?.userEmail || user.email
  const profileImage = profile?.profilePictureUrl || user.picture
  const initials = profileName
    ?.split(" ")
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase()

  return (
    <main className="tyk-page">
      <div className="tyk-page-shell">
        <section className="tyk-hero-panel">
          <div className="flex min-w-0 items-center gap-5">
            <Avatar className="h-24 w-24 border border-cyan-200/30 shadow-2xl">
              <AvatarImage src={profileImage} alt={profileName} />
              <AvatarFallback>{initials || "U"}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <span className="tyk-kicker">
                <UserRound size={16} aria-hidden="true" />
                Member profile
              </span>
              <h1 className="tyk-hero-title">{profileName}</h1>
              <p className="tyk-hero-copy">
                Your member identity connects your dashboard, groups, marketplace activity, and campaign participation.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/dashboard" className="tyk-action">
              <LayoutDashboard size={16} aria-hidden="true" />
              Dashboard
            </Link>
            <Link to="/settings" className="tyk-secondary-action">
              <Settings size={16} aria-hidden="true" />
              Settings
            </Link>
          </div>
        </section>

        <section className="tyk-stat-row mb-4">
          <ProfileStat label="Email" value={profileEmail} />
          <ProfileStat label="Profile status" value={profile ? "Complete" : "Auth profile only"} />
          <ProfileStat label="Account source" value={user?.sub ? "Authenticated" : "Unknown"} />
        </section>

        <section className="tyk-grid">
          <article className="tyk-panel">
            <span className="tyk-kicker">
              <Mail size={15} aria-hidden="true" />
              Contact
            </span>
            <p className="mt-2 text-slate-200">{profileEmail}</p>
            <p className="mt-3 text-sm leading-6 text-slate-400">
              This is the primary address TYK uses for account identity and profile readiness checks.
            </p>
          </article>
          <article className="tyk-panel">
            <span className="tyk-kicker">
              <BadgeCheck size={15} aria-hidden="true" />
              Readiness
            </span>
            <p className="mt-2 text-slate-200">{profile ? "Profile data is available." : "Only Auth0 profile data is available."}</p>
            <p className="mt-3 text-sm leading-6 text-slate-400">
              Complete profiles make group, marketplace, and campaign workflows easier for other members to trust.
            </p>
          </article>
          <article className="tyk-panel">
            <span className="tyk-kicker">
              <AtSign size={15} aria-hidden="true" />
              Identity
            </span>
            <p className="mt-2 break-all text-slate-200">{user?.sub}</p>
            <p className="mt-3 text-sm leading-6 text-slate-400">
              This secure identity links your activity across the application.
            </p>
          </article>
        </section>
      </div>
    </main>
  )
}

export default Profile
